import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/pages/Register";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(token ? "" : "This reset link is missing its token. Please request a new one.");

  // Referrer/cache protection — strip the token from URL history once submitted.
  useEffect(() => {
    if (done) window.history.replaceState({}, document.title, "/reset-password");
  }, [done]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) { setError("Missing reset token."); return; }
    if (pw.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (pw !== pw2) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/confirm-password-reset", { token, new_password: pw });
      setDone(true);
      toast.success("Password changed! Please sign in.");
      setTimeout(() => navigate("/login"), 2200);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-md mx-auto px-5 py-16" data-testid="reset-success">
        <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32]">All set</div>
        <h1 className="font-display text-4xl mt-2">Password updated</h1>
        <div className="mt-6 rounded-md bg-[#D4DEC6]/30 border border-[#2B3A67]/20 p-5 text-sm text-[#1A1F2C]">
          <div className="flex items-center gap-2 text-[#2B3A67] font-semibold mb-2">
            <CheckCircle2 size={18} /> Success
          </div>
          Your password has been changed. Redirecting you to sign in…
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-5 py-16">
      <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32]">Set a new password</div>
      <h1 className="font-display text-4xl mt-2">Choose your password</h1>
      <p className="text-[#5C6B7A] mt-3 leading-relaxed">
        Pick something at least 6 characters long. After saving, you&rsquo;ll be redirected to the login screen.
      </p>

      {!token && (
        <div className="mt-6 rounded-md bg-red-50 border border-red-200 p-4 text-sm text-red-700 flex items-start gap-2" data-testid="reset-no-token">
          <XCircle size={16} className="mt-0.5 shrink-0" />
          <div>
            This link doesn&rsquo;t contain a valid reset token.{" "}
            <Link to="/forgot-password" className="underline font-medium">Request a new reset link</Link>.
          </div>
        </div>
      )}

      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
        <div>
          <Label htmlFor="rp-pw">New password</Label>
          <PasswordInput
            id="rp-pw"
            required
            autoComplete="new-password"
            value={pw}
            onChange={(e) => { setPw(e.target.value); if (error) setError(""); }}
            testId="reset-password"
          />
        </div>
        <div>
          <Label htmlFor="rp-pw2">Confirm new password</Label>
          <PasswordInput
            id="rp-pw2"
            required
            autoComplete="new-password"
            value={pw2}
            onChange={(e) => { setPw2(e.target.value); if (error) setError(""); }}
            testId="reset-password-confirm"
          />
        </div>
        {error && <div className="text-sm text-red-600" data-testid="reset-error">{error}</div>}
        <Button
          type="submit"
          disabled={loading || !token}
          className="w-full bg-[#C85A32] hover:bg-[#a8482a] text-white rounded-md h-11"
          data-testid="reset-submit"
        >
          {loading ? "Saving…" : "Change password"}
        </Button>
      </form>

      <div className="mt-6 text-sm text-[#5C6B7A]">
        <Link to="/login" className="text-[#C85A32] font-medium" data-testid="reset-back-to-login">Back to login</Link>
      </div>
    </div>
  );
}
